// src/hooks/layout/layoutUtils.js
// Ortak layout yardımcı fonksiyonları — tüm pafta hook'ları tarafından kullanılır
import * as THREE from 'three';
import { dxfDrawCore } from '../../modules/dxf';
import { createDocumentHeaderParts, createMainFrameParts } from '../../utils/simpleTransformFunctions';

export const DOCUMENT_OFFSET = 50;
export const HEADER_HEIGHT = 50;
export const STANDARD_HEIGHTS = [310, 457, 620, 760, 860, 914, 1070];
export const MULTIPLIER = 5;

/**
 * MAIN_FRAME_HEIGHT hesapla — en yüksek doküman için uygun standart yükseklik seç.
 */
export function calculateMainFrameHeight(maxDocHeight) {
  for (const w of STANDARD_HEIGHTS) {
    if (maxDocHeight <= w * MULTIPLIER) {
      return w * MULTIPLIER;
    }
  }
  return STANDARD_HEIGHTS[STANDARD_HEIGHTS.length - 1] * MULTIPLIER;
}

/**
 * THREE.Group'u deep-clone eder ve orijinal renkleri geri yükler.
 */
export function cloneAndRestoreColors(group) {
  const cloned = group.clone(true);
  cloned.traverse((child) => {
    if (child.userData?.originalColor && child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((mat, index) => {
          if (mat.color && child.userData.originalColor[index] !== undefined) {
            mat.color.set(child.userData.originalColor[index]);
          }
        });
      } else if (child.material.color) {
        child.material.color.set(child.userData.originalColor);
      }
    }
  });
  return cloned;
}

/**
 * Klonlanmış grubu doküman alanı içinde merkezler.
 */
export function positionGroupInDocument(clonedGroup, docWidth, docHeight) {
  // Önceki layout'tan kalma pozisyonu sıfırla; bbox her zaman çizim
  // geometrisinin yerel koordinat sisteminde hesaplansın (deterministik).
  clonedGroup.position.set(0, 0, 0);
  const bbox = new THREE.Box3().setFromObject(clonedGroup);
  const center = bbox.getCenter(new THREE.Vector3());
  clonedGroup.position.sub(center);
  const layoutOffset = new THREE.Vector3(
    docWidth / 2,
    -docHeight / 2 - DOCUMENT_OFFSET,
    0
  );
  clonedGroup.position.add(layoutOffset);
}

/**
 * Kat planı dokümanlarını (+ opsiyonel vaziyet planı) sahneye render eder.
 * Tüm pafta hook'ları tarafından ortak kullanılır.
 *
 * @param {Object} params
 * @param {THREE.Scene} params.scene     - Hedef sahne
 * @param {Array}        params.floorPlans - Sıralı floor plan dizisi ({ id, name, width, height, group })
 * @param {Object|null}  params.sitePlan   - Vaziyet planı (opsiyonel)
 * @returns {{ mainFrameGroup, allGroups, bounds, documentBounds }} | null
 */
/**
 * THREE.Group ve altındaki tüm kaynakları (geometry, material, texture) dispose eder.
 */
export function disposeGroup(group) {
  if (!group) return;
  group.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }
    if (child.material) {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((mat) => {
        if (mat.map) mat.map.dispose();
        if (mat.lightMap) mat.lightMap.dispose();
        if (mat.bumpMap) mat.bumpMap.dispose();
        if (mat.normalMap) mat.normalMap.dispose();
        if (mat.specularMap) mat.specularMap.dispose();
        if (mat.envMap) mat.envMap.dispose();
        mat.dispose();
      });
    }
  });
}

export function renderFloorPlanDocuments({ scene, floorPlans, sitePlan }) {
  if (!floorPlans || floorPlans.length === 0) return null;

  // Group'u olan planları filtrele
  const validPlans = floorPlans.filter(p => p.group);
  if (validPlans.length === 0 && !sitePlan?.group) return null;

  // MAIN_FRAME_HEIGHT hesapla
  const allDocHeights = validPlans.map(p => p.height + 4 * DOCUMENT_OFFSET);
  if (sitePlan?.height) allDocHeights.push(sitePlan.height);
  const maxDocHeight = allDocHeights.length > 0 ? Math.max(...allDocHeights) : 0;
  const MAIN_FRAME_HEIGHT = calculateMainFrameHeight(maxDocHeight);
  const maxColumnHeight = MAIN_FRAME_HEIGHT - 2 * DOCUMENT_OFFSET;

  // Doküman tanımları oluştur
  const documentDefs = [];

  // Vaziyet planı (varsa)
  if (sitePlan?.width && sitePlan?.height && sitePlan?.group) {
    documentDefs.push({
      name: 'Vaziyet Planı',
      displayName: 'Vaziyet Planı',
      w: sitePlan.width,
      h: Math.min(sitePlan.height, maxColumnHeight),
      hasFrame: true,
      group: sitePlan.group,
    });
  }

  // Kat planları — her biri tam sütun yüksekliğinde
  validPlans.forEach(plan => {
    documentDefs.push({
      name: 'Kat Planı',
      displayName: plan.name || 'Kat Planı',
      w: plan.width + 2 * DOCUMENT_OFFSET,
      h: maxColumnHeight,
      hasFrame: true,
      floorPlanId: plan.id,
      group: plan.group,
    });
  });

  if (documentDefs.length === 0) return null;

  // ── Layout: sütun bazlı yerleşim ──
  const mainFrameGroup = new THREE.Group();
  const allGroups = [];
  const newDocumentBounds = [];
  const columnGroups = [[]];
  const columnHeights = [];
  let currentX = 0;
  let columnHeight = 0;
  let rowMaxWidth = 0;
  let maxX = 0;

  documentDefs.forEach(docDef => {
    const documentGroup = new THREE.Group();

    // Header (DXF entities)
    const headerName = docDef.displayName || docDef.name;
    const headerParts = createDocumentHeaderParts(
      headerName, docDef.w, docDef.h, HEADER_HEIGHT, { drawFrame: true }
    );
    if (headerParts.length > 0) {
      const contentGroup = new THREE.Group();
      dxfDrawCore(contentGroup, headerParts, [], 'scene');
      if (contentGroup.children.length > 0) {
        const bbox = new THREE.Box3().setFromObject(contentGroup);
        contentGroup.position.set(0, -bbox.max.y, 0);
        documentGroup.add(contentGroup);
      }
    }

    // Group klonla ve doküman içine yerleştir
    if (docDef.group) {
      const clonedGroup = cloneAndRestoreColors(docDef.group);
      positionGroupInDocument(clonedGroup, docDef.w, docDef.h);
      documentGroup.add(clonedGroup);
    }

    // Sütun taşma kontrolü
    const nextHeight = columnHeight + docDef.h + (columnHeight > 0 ? DOCUMENT_OFFSET : 0);
    if (nextHeight > maxColumnHeight) {
      columnHeights.push(columnHeight);
      columnGroups.push([]);
      currentX += rowMaxWidth + DOCUMENT_OFFSET;
      columnHeight = 0;
      rowMaxWidth = 0;
    }

    const yOffset = columnHeight + (columnHeight > 0 ? DOCUMENT_OFFSET : 0);
    documentGroup.position.set(currentX, -DOCUMENT_OFFSET - yOffset, 0);

    columnGroups[columnGroups.length - 1].push({
      group: documentGroup, docDef, w: docDef.w, h: docDef.h,
    });

    const docRightEdge = currentX + docDef.w;
    if (docRightEdge > maxX) maxX = docRightEdge;

    columnHeight = yOffset + docDef.h;
    rowMaxWidth = Math.max(rowMaxWidth, docDef.w);

    mainFrameGroup.add(documentGroup);
    allGroups.push(documentGroup);
  });

  // Bottom-align: her sütunu çerçeve altına hizala
  columnHeights.push(columnHeight);
  columnGroups.forEach((colDocs, ci) => {
    const colHeight = columnHeights[ci] || 0;
    const shift = maxColumnHeight - colHeight;
    if (shift > 0) {
      colDocs.forEach(item => { item.group.position.y -= shift; });
    }
  });

  // Doküman sınırları (snap/selection için)
  columnGroups.forEach(colDocs => {
    colDocs.forEach(item => {
      if (item.docDef.name === 'Kat Planı' && item.docDef.floorPlanId) {
        const pos = item.group.position;
        newDocumentBounds.push({
          floorPlanId: item.docDef.floorPlanId,
          name: item.docDef.displayName || item.docDef.name,
          minX: pos.x,
          maxX: pos.x + item.w,
          minY: pos.y - item.h,
          maxY: pos.y,
        });
      }
    });
  });

  // Ana çerçeve (main frame)
  const frameWidth = maxX + DOCUMENT_OFFSET;
  const mainFrameParts = createMainFrameParts(frameWidth, MAIN_FRAME_HEIGHT, DOCUMENT_OFFSET);
  const mainFrameContentGroup = new THREE.Group();
  dxfDrawCore(mainFrameContentGroup, mainFrameParts, [], 'scene');
  if (mainFrameContentGroup.children.length > 0) {
    mainFrameGroup.add(mainFrameContentGroup);
  }

  scene.add(mainFrameGroup);

  return {
    mainFrameGroup,
    allGroups,
    bounds: {
      frameWidth,
      frameHeight: MAIN_FRAME_HEIGHT,
      minX: 0,
      maxX: frameWidth,
      minY: -MAIN_FRAME_HEIGHT,
      maxY: 0,
    },
    documentBounds: newDocumentBounds,
  };
}
